import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Enumerables
import { TFunction } from 'i18next';
import { InteractionStatus } from '../../../types/InteractionStatus';
import { Namespace } from '../../../translation/namespace';

import { translatePartOfStringWithLink } from '../../../lib/StringUtils/StringHTMLUtils';

// Components
import Form, { FormResponseErrorType, FormUtils } from '../../molecules/Form';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { murmurhash } from '../../../lib/js/hash';
import { asFetchableView, useViewContext } from '../../../_lib/ViewFetcher';
import { useModalContext } from '../../organisms/ModalDialog/ModalContext';
import { useDelayed } from '../../../hooks/useDelayed';
import UserInviteFormTemplate from './UserInviteFormTemplate';
import { useNavigate, useLocation } from 'react-router';

enum Error {
  USER_ALREADY_MEMBER = 'userAlreadyMember',
  USER_ALREADY_REGISTERED = 'userAlreadyRegistered',
}

const mappedStatusMessages = (t: TFunction, i18n: any, translationNamespace: string, supportEmail?: string) => {
  const messages: any = {
    [InteractionStatus.FAIL]: FormUtils.createStatusMessage(t(`${translationNamespace}:inviteFail`)),
    [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t(`${translationNamespace}:inviteSuccess`)),
    [FormResponseErrorType.INVITED_USER_ALREADY_REGISTERED]: FormUtils.createStatusMessage(
      t(`${Namespace.Form}:errorType.${FormResponseErrorType.INVITED_USER_ALREADY_REGISTERED}`),
    ),
    [FormResponseErrorType.EMAIL_DOMAIN_NOT_ALLOWED]: FormUtils.createStatusMessage(
      translatePartOfStringWithLink(
        `${Namespace.Form}:errorType.${FormResponseErrorType.EMAIL_DOMAIN_NOT_ALLOWED}`,
        supportEmail ? `mailto:${supportEmail}` : null,
      ),
    ),
  };

  if (i18n.exists(`${Namespace.Form}:errorType.${Error.USER_ALREADY_MEMBER}`)) {
    messages[Error.USER_ALREADY_MEMBER] = FormUtils.createStatusMessage(
      t(`${Namespace.Form}:errorType.${Error.USER_ALREADY_MEMBER}`),
      InteractionStatus.FAIL,
    );
  }

  return messages;
};

type TeamroomInviteType = {
  form: any;
  members?: {
    ids: string[];
  };
};

function TeamroomInvite({ form, members }: TeamroomInviteType) {
  const [error, setError] = useState<Error | undefined>(undefined);
  const { t, i18n } = useTranslation();
  const { portalConfiguration } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const { additionalProps } = useViewContext();
  const { translationNamespace } = additionalProps;

  const { closeModal } = useModalContext();
  const delayedModalClose = useDelayed(() => {
    closeModal();
    navigate(location.pathname);
  });

  const onSubmit = (event: any, model: any) => {
    event.preventDefault();

    // also resetting state before submitting
    if (error) {
      setError(undefined);
    }
    // pure frontend validation, members.id in json and only used in 'invite' form
    if (model.parameter.recipientId) {
      if (members?.ids?.includes(model.parameter.recipientId.value)) {
        setError(Error.USER_ALREADY_MEMBER);
        return;
      }
    }

    model
      .submit()
      .then(() => {
        if (model.status === InteractionStatus.SUCCESS) {
          delayedModalClose();
        }
      })
      .catch();
  };

  return (
    <Form id="teamroom-invite-form" model={form} onSubmit={onSubmit}>
      <UserInviteFormTemplate
        id={murmurhash(form.url)}
        statusMessages={mappedStatusMessages(
          t,
          i18n,
          translationNamespace,
          portalConfiguration?.son_portal_support_email,
        )}
        error={error}
        headline={t(`${translationNamespace}:headline`)}
        userMail={{
          label: t(`${Namespace.glossary}:eMailAddress`),
          helpText: t(`${translationNamespace}:chooseUserHelpText`),
          patternMismatchErrorMessage: t(`${translationNamespace}:emailNotValid`),
        }}
        userSelect={{
          label: t(`${translationNamespace}:chooseUser`),
          helpText: t(`${translationNamespace}:chooseUserHelpText`),
          validationMessage: t(`${translationNamespace}:validationMessage`),
        }}
        roleSelect={{ label: t(`${translationNamespace}:roleName`) }}
        message={{ label: t(`${Namespace.glossary}:message`) }}
        submit={{ name: t(`${translationNamespace}:sendInvite`) }}
      />
    </Form>
  );
}

export const TeamroomInviteView = asFetchableView(TeamroomInvite);
